import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Menu as MenuIcon, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import MonthView from '../components/MonthView';
import WeekView from '../components/WeekView';
import WeeksOverview from '../components/WeeksOverview';
import QuartersOverview from '../components/QuartersOverview';
import QuarterView from '../components/QuarterView';
import TotalOverview from '../components/TotalOverview';
import Reports from '../components/Reports';
import DayOverview from '../components/DayOverview';
import FixedExpenses from '../components/FixedExpenses';
import Settings from './Settings';
import Sidebar from '../components/Sidebar';
import MonthsOverview from '../components/MonthsOverview';
import MobileNavigation from '../components/MobileNavigation';
import TopNavbar from '../components/TopNavbar';
import HiddenRevenue from '../components/HiddenRevenue';
import ComparisonView from '../components/ComparisonView';

export default function Dashboard() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Reset scroll position when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-900 overflow-hidden">
      {/* Mobile-only top navbar */}
      <div className="lg:hidden">
        <TopNavbar
          isMenuOpen={sidebarOpen}
          onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Spacer div to offset main content */}
        <div className="hidden lg:block w-64 flex-shrink-0" />
        
        {/* Sidebar - fixed on desktop, sliding on mobile */}
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onMonthSelect={(date) => {
            navigate(`/month/${format(date, 'yyyy/MM')}`);
            setSidebarOpen(false);
          }}
          onWeekSelect={(year, week) => {
            navigate(`/weeks/${year}`);
            setSidebarOpen(false);
          }}
          onQuarterSelect={(year, quarter) => {
            navigate(`/quarters/${year}`);
            setSidebarOpen(false);
          }}
          onTotalOverviewSelect={() => {
            navigate('/total');
            setSidebarOpen(false);
          }}
          onReportsSelect={() => {
            navigate('/reports');
            setSidebarOpen(false);
          }}
          onDayOverviewSelect={() => {
            navigate('/days');
            setSidebarOpen(false);
          }}
          onSettingsSelect={() => {
            navigate('/settings');
            setSidebarOpen(false);
          }}
          onSignOut={handleSignOut}
        />

        {/* Main content - scrollable */}
        <main className="flex-1 overflow-y-auto">
          <div className="px-4 py-4 lg:px-8 pb-32 lg:pb-8 min-h-full">
            <Routes>
              <Route path="/month/:year/:month" element={<MonthView />} />
              <Route path="/week/:year/:week" element={<WeekView />} />
              <Route path="/weeks/:year" element={<WeeksOverview />} />
              <Route path="/quarters/:year" element={<QuartersOverview />} />
              <Route path="/quarter/:year/:quarter" element={<QuarterView />} />
              <Route path="/total" element={<TotalOverview />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/days" element={<DayOverview />} />
              <Route path="/expenses" element={<FixedExpenses />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/hidden" element={<HiddenRevenue />} />
              <Route path="/compare" element={<ComparisonView />} />
              <Route path="/months" element={<MonthsOverview />} />
              <Route
                path="*"
                element={<Navigate to="/total" replace />} />
            </Routes>
          </div>
        </main>
      </div>
      
      {/* Mobile-only bottom navigation */}
      <div className="lg:hidden">
        <MobileNavigation />
      </div>
    </div>
  );
}